import React, { useMemo, useState, useCallback } from 'react';
import { Annotation } from '@/services/annotationService';
import { useFloorPlanScale } from '@/hooks/useFloorPlanScale';
import { calculateDistance } from '@/utils/scaleCalculations';
import { useAnnotations, useMeasurementOverride } from '@/hooks/useAnnotations';

interface MeasurementData {
  start: { x: number; y: number };
  end: { x: number; y: number };
  manualValue?: string;
}

interface MeasurementAnnotationProps {
  annotation: Annotation;
  isSelected: boolean;
  isHovered: boolean;
  scale: number;
  onClick: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  isInteractive: boolean;
  floorPlanId: string;
}

export function MeasurementAnnotation({
  annotation,
  isSelected,
  isHovered,
  scale: _scale,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onTouchStart,
  onTouchEnd,
  onTouchMove,
  isInteractive,
  floorPlanId,
}: MeasurementAnnotationProps) {
  // Prefer the MeasurementProvider override (set by ElevationAnnotationLayer
  // with the elevation's calibration) over the floor plan's scale. Without
  // this, a measurement on an elevation read scale from the wrong source
  // (the floor plan's pixelsPerUnit) and the value displayed wrong — e.g. a
  // 3.1 m wall measurement showed up as 2.63 m because the floor plan and
  // elevation calibrations differed.
  const measurementOverride = useMeasurementOverride();
  const { formatMeasurement: fpFormatMeasurement, hasScale: fpHasScale } = useFloorPlanScale(
    floorPlanId || '',
  );
  const formatMeasurement = measurementOverride?.formatMeasurement ?? fpFormatMeasurement;
  const hasScale = measurementOverride?.hasScale ?? fpHasScale;
  // Use annotation.floor_plan_id (which holds the elevation id when the
  // annotation lives on an elevation, after the useAnnotations hook's
  // elevation_items → Annotation transform) so updates land in the SAME
  // React Query cache that ElevationAnnotationLayer reads from. Using the
  // floorPlanId prop here keys the cache by the actual floor plan id and
  // resizing on the elevation never updates the visible annotation.
  const { updateAnnotation } = useAnnotations(annotation.floor_plan_id);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<'start' | 'end' | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [previewEndpoints, setPreviewEndpoints] = useState<{
    start: { x: number; y: number };
    end: { x: number; y: number };
  } | null>(null);

  const savedData = annotation.data as MeasurementData;
  const data = useMemo(
    () =>
      previewEndpoints
        ? { ...savedData, start: previewEndpoints.start, end: previewEndpoints.end }
        : savedData,
    [previewEndpoints, savedData],
  );

  // Calculate distance and format it
  const measurement = useMemo(() => {
    if (!data?.start || !data?.end) return '';
    // Manual override takes priority
    if (data.manualValue) {
      return data.manualValue;
    }
    const distance = calculateDistance(data.start, data.end);
    // Only show measurement if we have scale data, otherwise show nothing
    return hasScale ? formatMeasurement(distance) : '';
  }, [data, hasScale, formatMeasurement]);

  // Calculate midpoint for text placement
  const midX = (data.start.x + data.end.x) / 2;
  const midY = (data.start.y + data.end.y) / 2;

  // Calculate angle for text rotation
  const angle = Math.atan2(data.end.y - data.start.y, data.end.x - data.start.x) * (180 / Math.PI);
  const shouldFlipText = angle > 90 || angle < -90;
  const textAngle = shouldFlipText ? angle + 180 : angle;

  // Read style.color to match the contextual toolbar (which writes `color`)
  // and the other annotation components. Fall back to the legacy
  // `strokeColor` field for any pre-existing measurements that still have
  // it set, then to the default blue.
  const strokeColor = annotation.style?.color || annotation.style?.strokeColor || '#3b82f6';
  const strokeWidth = annotation.style?.strokeWidth || 2;
  const fontSize = annotation.style?.fontSize || 14;

  // Handle resize
  const handleResizeStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent, handle: 'start' | 'end') => {
      if (!isInteractive || !isSelected) return;

      e.stopPropagation();
      e.preventDefault();

      setIsResizing(true);
      setResizeHandle(handle);

      // Get the SVG element
      const svg = (e.target as Element).closest('svg');
      if (!svg) return;

      const originalStart = { ...data.start };
      const originalEnd = { ...data.end };

      // Track last position for final update
      let lastStart = originalStart;
      let lastEnd = originalEnd;

      const handleMove = (clientX: number, clientY: number, shiftKey: boolean) => {
        // Convert mouse position to SVG coordinates
        const pt = svg.createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        const currentSvgCoords = pt.matrixTransform(svg.getScreenCTM()?.inverse());

        let newStart = originalStart;
        let newEnd = originalEnd;

        if (handle === 'start') {
          if (shiftKey) {
            // Constrain to horizontal or vertical based on angle to other point
            const dx = Math.abs(currentSvgCoords.x - originalEnd.x);
            const dy = Math.abs(currentSvgCoords.y - originalEnd.y);

            if (dx > dy) {
              // Horizontal constraint
              newStart = { x: currentSvgCoords.x, y: originalEnd.y };
            } else {
              // Vertical constraint
              newStart = { x: originalEnd.x, y: currentSvgCoords.y };
            }
          } else {
            // Direct positioning to mouse location
            newStart = {
              x: currentSvgCoords.x,
              y: currentSvgCoords.y,
            };
          }
        } else {
          if (shiftKey) {
            // Constrain to horizontal or vertical based on angle to other point
            const dx = Math.abs(currentSvgCoords.x - originalStart.x);
            const dy = Math.abs(currentSvgCoords.y - originalStart.y);

            if (dx > dy) {
              // Horizontal constraint
              newEnd = { x: currentSvgCoords.x, y: originalStart.y };
            } else {
              // Vertical constraint
              newEnd = { x: originalStart.x, y: currentSvgCoords.y };
            }
          } else {
            // Direct positioning to mouse location
            newEnd = {
              x: currentSvgCoords.x,
              y: currentSvgCoords.y,
            };
          }
        }

        // Store the last values
        lastStart = newStart;
        lastEnd = newEnd;

        setPreviewEndpoints({ start: newStart, end: newEnd });
      };

      const handleMouseMove = (e: MouseEvent) => {
        e.preventDefault();
        handleMove(e.clientX, e.clientY, e.shiftKey);
      };

      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault();
        if (e.touches.length === 1) {
          handleMove(e.touches[0].clientX, e.touches[0].clientY, false);
        }
      };

      const handleEnd = () => {
        setIsResizing(false);
        setResizeHandle(null);

        // Create final update with history if values changed
        if (
          lastStart.x !== originalStart.x ||
          lastStart.y !== originalStart.y ||
          lastEnd.x !== originalEnd.x ||
          lastEnd.y !== originalEnd.y
        ) {
          updateAnnotation({
            id: annotation.id,
            data: {
              data: {
                ...annotation.data,
                start: lastStart,
                end: lastEnd,
              },
            },
            skipHistory: false, // Create history entry for the final position
          }).finally(() => setPreviewEndpoints(null));
        } else {
          setPreviewEndpoints(null);
        }

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleEnd);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
    },
    [annotation, updateAnnotation, isInteractive, isSelected, data.start, data.end],
  );

  // Handle double-click to edit measurement
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!isInteractive || !isSelected) return;
      e.stopPropagation();
      setIsEditing(true);
      setEditValue(data.manualValue || measurement);
    },
    [isInteractive, isSelected, data.manualValue, measurement],
  );

  // Save manual override
  const handleSaveOverride = useCallback(async () => {
    setIsEditing(false);
    const trimmed = editValue.trim();

    // Calculate the auto-calculated value for comparison
    const autoCalculated =
      hasScale && data?.start && data?.end
        ? formatMeasurement(calculateDistance(data.start, data.end))
        : '';

    // If empty or same as auto-calculated, remove manual override
    if (!trimmed || trimmed === autoCalculated) {
      if (data.manualValue) {
        await updateAnnotation({
          id: annotation.id,
          data: { data: { ...data, manualValue: undefined } },
        });
      }
      return;
    }

    // Save manual override
    await updateAnnotation({
      id: annotation.id,
      data: { data: { ...data, manualValue: trimmed } },
    });
  }, [editValue, data, annotation.id, updateAnnotation, hasScale, formatMeasurement]);

  // Handle key events while editing the inline value.
  // Stop propagation on every key so global handlers (Delete/Backspace deleting
  // the annotation, Cmd+D duplicating it, etc.) don't fire while typing.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSaveOverride();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setIsEditing(false);
        setEditValue('');
      }
    },
    [handleSaveOverride],
  );

  // Early return after all hooks
  if (!data?.start || !data?.end) return null;

  return (
    <g
      data-annotation-id={annotation.id}
      onClick={isInteractive ? onClick : undefined}
      onMouseEnter={isInteractive ? onMouseEnter : undefined}
      onMouseLeave={isInteractive ? onMouseLeave : undefined}
      onMouseDown={isInteractive ? onMouseDown : undefined}
      onTouchStart={isInteractive ? onTouchStart : undefined}
      onTouchEnd={isInteractive ? onTouchEnd : undefined}
      onTouchMove={isInteractive ? onTouchMove : undefined}
      style={{
        cursor: isInteractive ? 'pointer' : 'default',
        pointerEvents: isInteractive ? 'auto' : 'none',
        filter: isHovered ? 'url(#glow)' : undefined,
      }}
      opacity={annotation.style?.opacity || 1}
    >
      {/* Invisible hit area for easier selection and dragging */}
      <line
        x1={data.start.x}
        y1={data.start.y}
        x2={data.end.x}
        y2={data.end.y}
        stroke="transparent"
        strokeWidth={Math.max(20, strokeWidth * 4)}
        pointerEvents={isInteractive ? 'auto' : 'none'}
        style={{ cursor: isInteractive ? 'move' : 'default' }}
      />

      {/* Measurement line */}
      <line
        x1={data.start.x}
        y1={data.start.y}
        x2={data.end.x}
        y2={data.end.y}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        pointerEvents="none"
      />

      {/* End caps */}
      <line
        x1={data.start.x - 5}
        y1={data.start.y}
        x2={data.start.x + 5}
        y2={data.start.y}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        transform={`rotate(${angle + 90} ${data.start.x} ${data.start.y})`}
        pointerEvents="none"
      />
      <line
        x1={data.end.x - 5}
        y1={data.end.y}
        x2={data.end.x + 5}
        y2={data.end.y}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        transform={`rotate(${angle + 90} ${data.end.x} ${data.end.y})`}
        pointerEvents="none"
      />

      {/* Measurement text background */}
      {measurement && !isEditing && (
        <rect
          x={measurement.length * -4}
          y={-10}
          width={measurement.length * 8}
          height={20}
          fill="white"
          fillOpacity={0.9}
          rx={4}
          transform={`translate(${midX}, ${midY}) rotate(${textAngle})`}
          pointerEvents="none"
        />
      )}

      {/* Measurement text */}
      {measurement && !isEditing && (
        <text
          x={midX}
          y={midY}
          fill={data.manualValue ? '#f97316' : strokeColor}
          fontSize={fontSize}
          fontWeight="bold"
          textAnchor="middle"
          dominantBaseline="middle"
          transform={`rotate(${textAngle} ${midX} ${midY})`}
          style={{
            userSelect: 'none',
            cursor: isInteractive && isSelected ? 'text' : 'default',
          }}
          pointerEvents={isInteractive && isSelected ? 'auto' : 'none'}
          onDoubleClick={handleDoubleClick}
        >
          {measurement}
          {data.manualValue ? '*' : ''}
        </text>
      )}

      {/* Inline edit input */}
      {isEditing && (
        <foreignObject
          x={midX - 60}
          y={midY - 15}
          width={120}
          height={30}
          transform={`rotate(${textAngle} ${midX} ${midY})`}
        >
          <input
            type="text"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={handleSaveOverride}
            onKeyDown={handleKeyDown}
            autoFocus
            // Select the entire current value on focus so the user can
            // immediately start typing to replace it.
            onFocus={e => e.currentTarget.select()}
            style={{
              width: '100%',
              height: '100%',
              border: '2px solid #3b82f6',
              borderRadius: '4px',
              padding: '4px 8px',
              fontSize: `${fontSize}px`,
              fontWeight: 'bold',
              textAlign: 'center',
              outline: 'none',
            }}
          />
        </foreignObject>
      )}

      {/* Resize handles */}
      {isSelected && isInteractive && (
        <>
          <circle
            cx={data.start.x}
            cy={data.start.y}
            r={6}
            fill={strokeColor}
            stroke="white"
            strokeWidth={2}
            style={{ cursor: 'move', pointerEvents: 'auto' }}
            opacity={isResizing && resizeHandle === 'start' ? 0 : 1}
            onMouseDown={e => handleResizeStart(e, 'start')}
            onTouchStart={e => handleResizeStart(e, 'start')}
          />
          <circle
            cx={data.end.x}
            cy={data.end.y}
            r={6}
            fill={strokeColor}
            stroke="white"
            strokeWidth={2}
            style={{ cursor: 'move', pointerEvents: 'auto' }}
            opacity={isResizing && resizeHandle === 'end' ? 0 : 1}
            onMouseDown={e => handleResizeStart(e, 'end')}
            onTouchStart={e => handleResizeStart(e, 'end')}
          />
        </>
      )}
    </g>
  );
}
